import { consoleRoutes } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger, AppLink, Badge, Button, EmptyState, Icon, Skeleton, toast } from "@sagot/ui";
import { useErrorMessage } from "../../../lib/api-errors";
import { useWorkspaceContext } from "../../../session/workspace-context";
import type { EvalCaseResult, EvalRun } from "../shared/types";
import { useEvalMutations, useRunCases } from "./use-evals";

type Bag = Record<string, unknown>;
const text = (bag: Bag, key: string) => (typeof bag[key] === "string" ? bag[key] : null);
const num = (bag: Bag, key: string) => (typeof bag[key] === "number" ? bag[key] : null);

function Retrieved({ result }: { result: EvalCaseResult }) {
  const { t } = useTranslation();
  const rows = result.retrieved as Bag[];
  if (rows.length === 0) return <p className="mt-1 text-sm text-fg-muted">—</p>;
  return (
    <ul className="mt-1 space-y-1.5 text-sm">
      {rows.map((row, i) => (
        <li key={`${text(row, "source_id") ?? ""}-${String(i)}`} className="flex items-center gap-2 rounded-md border border-line bg-surface px-3 py-2">
          <span className="cite">{i + 1}</span>
          <span className="truncate">{text(row, "title") ?? text(row, "source") ?? t("build.evaluations.failed.source")}</span>
          <span className="ml-auto font-mono text-xs text-fg-muted">{num(row, "score")?.toFixed(2) ?? ""}</span>
        </li>
      ))}
    </ul>
  );
}

function FixLinks({ result, agentId, canManage, onAccept, accepting }: { result: EvalCaseResult; agentId: string; canManage: boolean; onAccept: () => void; accepting: boolean }) {
  const { t } = useTranslation();
  const { summary } = useWorkspaceContext();
  const sourceId = (result.retrieved as Bag[]).map((r) => text(r, "source_id")).find(Boolean);
  return (
    <div className="flex flex-wrap gap-2">
      {sourceId ? (
        <Button asChild variant="secondary" size="sm"><AppLink href={consoleRoutes.sourceDetail(summary.slug, sourceId)}><Icon name="database" />{t("build.evaluations.failed.fixSource")}</AppLink></Button>
      ) : (
        <Button asChild variant="secondary" size="sm"><AppLink href={consoleRoutes.knowledge(summary.slug, agentId)}><Icon name="database" />{t("build.evaluations.failed.fixKnowledge")}</AppLink></Button>
      )}
      <Button asChild variant="ghost" size="sm"><AppLink href={consoleRoutes.agentSettings(summary.slug, agentId, "instructions")}><Icon name="pencil" />{t("build.evaluations.failed.fixInstructions")}</AppLink></Button>
      {result.accepted_at ? <Badge tone="success"><Icon name="check" className="size-3" />{t("build.evaluations.failed.acceptedBadge")}</Badge> : canManage && result.actual_answer ? (
        <Button variant="ghost" size="sm" loading={accepting} onClick={onAccept}>{t("build.evaluations.failed.accept")}</Button>
      ) : null}
    </div>
  );
}

export function FailedCases({ run, agentId, canManage }: { run: EvalRun; agentId: string; canManage: boolean }) {
  const { t } = useTranslation();
  const message = useErrorMessage();
  const cases = useRunCases(run.id);
  const { accept } = useEvalMutations(agentId);
  return (
    <section className="card" aria-labelledby="failed-cases-title">
      <div className="card-header">
        <div>
          <h2 id="failed-cases-title" className="card-title flex items-center gap-2"><Icon name="x-circle" className="size-4 text-danger" />{t("build.evaluations.failed.title", { number: run.number })}</h2>
          <p className="mt-0.5 text-xs text-fg-subtle">{t("build.evaluations.failed.subtitle", { fail: run.fail_count, partial: run.partial_count })}</p>
        </div>
      </div>
      {cases.isPending ? <div className="space-y-2 p-5" aria-hidden="true">{[0, 1, 2].map((i) => <Skeleton key={i} className="h-12" />)}</div> : null}
      {cases.data?.length === 0 ? <EmptyState icon="check-circle" headingLevel={3} title={t("build.evaluations.failed.none")} /> : null}
      {cases.data && cases.data.length > 0 ? (
        <Accordion type="multiple" className="divide-y divide-line">
          {cases.data.map((result) => (
            <AccordionItem key={result.id} value={result.id}>
              <AccordionTrigger className="flex w-full items-center gap-3 px-5 py-3.5 text-left hover:bg-surface-2">
                <Badge tone={result.verdict === "fail" ? "danger" : "warning"} className="w-16 shrink-0 justify-center">{t(`build.evaluations.failed.verdicts.${result.verdict}`, { defaultValue: result.verdict })}</Badge>
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-semibold">{result.question}</span>
                  {result.failure_category ? <span className="block text-xs text-fg-subtle">{result.failure_category}</span> : null}
                </span>
              </AccordionTrigger>
              <AccordionContent className="grid grid-cols-1 gap-4 bg-surface-2 px-5 py-4 lg:grid-cols-2">
                <div className="space-y-3">
                  <div><h3 className="text-[11px] font-bold uppercase tracking-wide text-fg-subtle">{t("build.evaluations.failed.expected")}</h3><p className="mt-1 rounded-md border border-line bg-surface p-3 text-sm">{result.expected_answer ?? "—"}</p></div>
                  <div><h3 className="text-[11px] font-bold uppercase tracking-wide text-fg-subtle">{t("build.evaluations.failed.actual")}</h3><p className="mt-1 rounded-md border border-line bg-danger-soft p-3 text-sm">{result.actual_answer ?? t("build.evaluations.failed.noAnswer")}</p></div>
                </div>
                <div className="space-y-3">
                  <div><h3 className="text-[11px] font-bold uppercase tracking-wide text-fg-subtle">{t("build.evaluations.failed.retrieved")}</h3><Retrieved result={result} /></div>
                  <div><h3 className="text-[11px] font-bold uppercase tracking-wide text-fg-subtle">{t("build.evaluations.failed.reasoning")}</h3><p className="mt-1 text-sm text-fg-muted">{result.judge_reasoning ?? t("build.evaluations.failed.noReasoning")}</p></div>
                  <FixLinks
                    result={result}
                    agentId={agentId}
                    canManage={canManage}
                    accepting={accept.isPending && accept.variables.resultId === result.id}
                    onAccept={() => { accept.mutate({ caseId: result.case_id, resultId: result.id }, { onSuccess: () => { toast.success(t("build.evaluations.failed.accepted")); void cases.refetch(); }, onError: (e) => { toast.error(message(e)); } }); }}
                  />
                </div>
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      ) : null}
    </section>
  );
}
